import { useEffect, useCallback, useState } from "react"
import useEmblaCarousel from "embla-carousel-react"
import { mq } from "helpers/styled"

const addQueries = options =>
  options.map(opt => ({
    ...opt,
    query: typeof window !== "undefined" && window.matchMedia(mq(opt.size))
  }))

export default function useEmbla(baseOptions, responsiveOptions = [], plugins = []) {
  const [currentSize, setCurrentSize] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState([])
  const [responsive] = useState(() => addQueries(responsiveOptions))

  const getCurrentOptions = useCallback(() => {
    const firstMatch = responsive.find(opt => opt.query?.matches)
    return firstMatch ? { ...baseOptions, ...firstMatch } : baseOptions
  }, [baseOptions, responsive])

  const [emblaRef, embla] = useEmblaCarousel(getCurrentOptions(), plugins)

  useEffect(() => {
    if (!embla) return undefined

    const listeners = {}

    responsive.forEach(({ query, size, ...rest }, i) => {
      const onChange = q => {
        if (q.matches) {
          if (responsive.find(x => x.query?.matches)?.size === size) {
            embla.reInit({ ...baseOptions, ...rest })
          }
        } else if (i === responsive.length - 1 && !responsive.find(x => x.query?.matches)) {
          embla.reInit(baseOptions)
        }

        setScrollSnaps(embla.scrollSnapList())
        setSelectedIndex(embla.selectedScrollSnap())
        setCurrentSize(size)
      }

      listeners[size] = onChange
      query.addListener(onChange)
    })

    return () => responsive.forEach(({ query, size }) => query.removeListener(listeners[size]))
  }, [responsive, embla])

  const handleScroll = useCallback(() => {
    setSelectedIndex(embla.selectedScrollSnap())
  }, [embla, setSelectedIndex])

  useEffect(() => {
    if (!embla) return undefined

    setScrollSnaps(embla.scrollSnapList())

    embla.on("scroll", handleScroll)

    return () => {
      embla.off("scroll", handleScroll)
    }
  }, [embla, handleScroll, currentSize])

  return {
    emblaRef,
    embla,
    selectedIndex,
    scrollSnaps
  }
}

export function useEmblaArrows(embla) {
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)

  const scrollPrev = useCallback(() => embla.scrollPrev(), [embla])
  const scrollNext = useCallback(() => embla.scrollNext(), [embla])

  useEffect(() => {
    if (!embla) return undefined

    const updateNav = () => {
      setPrevBtnEnabled(embla.canScrollPrev())
      setNextBtnEnabled(embla.canScrollNext())
    }

    updateNav()

    embla.on("select", updateNav)

    return () => embla.off("select", updateNav)
  }, [embla])

  return {
    prevProps: {
      handleClick: scrollPrev,
      disabled: !prevBtnEnabled
    },
    nextProps: {
      handleClick: scrollNext,
      disabled: !nextBtnEnabled
    }
  }
}
